import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './SelectEgg.module.scss';
import { AnimatePresence, m } from 'framer-motion';
import { HatcheningState, MAX_CONCURRENT_HATCH_LIMIT, useHatchening } from '../state-machine';
import { EggPreviewAnimatedCard, EggPreviewCard } from '../PreviewCard';
import { SelectableEggCardCollection } from 'components/SelectableCard/Variants/SelectableCardEgg';
import { SelectableCardPlaceholder } from 'components/SelectableCard/Variants/SelectableCardPlaceholder';
import { Button } from 'components/Button/Button';
import { InfoIcon } from 'components/Icons/Icons';
import { EggHatchingWarningHatchening } from '../../Collection/Disclaimers/Disclaimers';
import { HatcheningStep } from '../xstate-helpers';
import ReactPerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { HoldButton } from '../../Button/HoldButton';
import { HorizontalDivider } from '../../HorizontalDivider/HorizontalDivider';
import { MobileEggSelection } from '../MobileEggSelection';
import cls from 'classnames';
import { CollectionEmptyState } from '../../Collection/CollectionEmptyState';
import { HatchingModal } from '../HatchingModal';
import { NoCardSelected } from './NoCardSelected';
import throttle from 'lodash/throttle';

const LOAD_MORE_EGGS_TIMEOUT_MS = 500;

const placeholders = Array.from({ length: 8 }, (_, i) => {
  return <SelectableCardPlaceholder key={'PlaceholderKey' + i} maxWidth={50} />;
});

function decideMargin(width: number, selectedCards: number): number {
  let coefficient: number;
  if (width > 1200) {
    coefficient = -90;
  } else if (width > 900) {
    coefficient = -120;
  } else if (width > 750) {
    coefficient = -140;
  } else {
    // if the screen is really small we don't want scaling
    // we just want cards to be incredibly tight together
    // so they don't overflow from the screen
    return -255;
  }
  return coefficient * (selectedCards / 3);
}

function PreviewStack() {
  const [state, send] = useHatchening();
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    function onResize() {
      setWidth(window.innerWidth);
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onAnimationFinish = useCallback(() => {
    send({ type: 'animationEnd' });
  }, [send]);

  const selected = state.context.eggs.filter(({ selected }) => selected);
  return selected.length === 0 ? (
    <NoCardSelected />
  ) : (
    <AnimatePresence>
      {selected.map(({ item: egg }, i) => {
        const defaultMargin = decideMargin(width, selected.length);
        const marginLeft = i > 0 ? defaultMargin : 0;
        // we want the first card to simply appear instead of sliding
        // from the left.
        const initialMargin = selected.length === 1 ? 0 : -270;
        return (
          <m.li
            key={'AnimateListElement' + egg.id}
            className={styles.card}
            transition={{ type: 'tween' }}
            animate={{
              marginLeft,
              opacity: 1,
            }}
            exit={{
              marginLeft: initialMargin,
              opacity: 0,
            }}
            initial={{
              opacity: 0,
              marginLeft: initialMargin,
            }}
          >
            <HatcheningStep steps={['selectingEggs', 'hatchingEggs']}>
              <EggPreviewCard image={egg.image.thumbnail} level={egg.level} id={Number(egg.id)} />
            </HatcheningStep>
            <HatcheningStep steps="playingAnimation">
              <EggPreviewAnimatedCard
                onAnimationFinish={onAnimationFinish}
                image={egg.image.thumbnail}
                level={egg.level}
                id={Number(egg.id)}
              />
            </HatcheningStep>
          </m.li>
        );
      })}
    </AnimatePresence>
  );
}

function HatchDisclaimer() {
  return (
    <div className={styles.hatchDisclaimer}>
      <InfoIcon />
      <p className="m-0 body-default text-60">
        Once hatched, this action <em className="text-100">cannot be undone</em>
      </p>
    </div>
  );
}

function PreviewButtonSection() {
  const [state, send] = useHatchening();

  const unhatchable = state.context.hatchingStatus.type === 'unhatchableEgg';
  const invalid = state.context.hatchingStatus.type !== 'valid';
  return (
    <div className={styles.buttonSection}>
      <HoldButton
        primary={true}
        disabled={invalid}
        className={styles.button}
        success={() => send({ type: 'holdToHatch' })}
      >
        {unhatchable ? 'Hatch in progress...' : 'Hold to hatch'}
      </HoldButton>
      {unhatchable ? (
        <EggHatchingWarningHatchening className={styles.hatchDisclaimer} />
      ) : (
        <HatchDisclaimer />
      )}
      <div className={cls(styles.mobileVisible, styles.button)}>
        <HorizontalDivider />
        <DrawerButton />
      </div>
    </div>
  );
}

function Preview() {
  const [state] = useHatchening();
  const center = state.matches('playingAnimation') || state.matches('hatchingEggs');
  return (
    <article className={cls(styles.preview, center && styles.centered)}>
      <ul className={styles.cardStack}>
        <PreviewStack />
      </ul>
      <AnimatePresence>
        <HatcheningStep steps={['loadingEggs', 'selectingEggs']}>
          <m.div
            key="preview-button"
            style={{ marginTop: '24px' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ type: 'tween' }}
          >
            <PreviewButtonSection />
          </m.div>
        </HatcheningStep>
      </AnimatePresence>
    </article>
  );
}

function Sidebar() {
  const [state, send] = useHatchening();

  const fetchMoreEggs = useCallback(() => {
    return send({ type: 'loadMoreEggs' });
  }, [send]);
  // using throttle here because `onYReachEnd` will be called endlessly
  // upon reaching the end of the list and we need a way to prevent it
  // from sending unnecessary events to the state machine
  const fetchMore = useRef(throttle(fetchMoreEggs, LOAD_MORE_EGGS_TIMEOUT_MS)).current;
  const selectedCount = state.context.eggs.filter((x) => x.selected).length;

  return (
    <ReactPerfectScrollbar className={styles.sidebar} onYReachEnd={fetchMore}>
      {state.context.eggs.map(({ item: egg, selected }, i) => (
        <SelectableEggCardCollection
          card={SelectableEggCardCollection.transformer(egg)}
          isSelected={selected}
          onClick={() => send({ type: 'toggleEgg', egg })}
          key={'SelectableEggCard' + egg.id + '-' + i}
          isDisabled={!selected && selectedCount === MAX_CONCURRENT_HATCH_LIMIT}
        />
      ))}
      <HatcheningStep steps="loadingEggs">{placeholders}</HatcheningStep>
    </ReactPerfectScrollbar>
  );
}

function DrawerButton() {
  const [, send] = useHatchening();
  return (
    <Button onClick={() => send({ type: 'toggleDrawer' })} className={styles.button}>
      Select Eggs
    </Button>
  );
}

export function SelectEgg() {
  const [state] = useHatchening();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Select Starting Egg Based on Query Parameter
    const searchedEgg = searchParams.get('egg');
    if (searchedEgg !== null) {
      state.context.defaultEggId = parseInt(searchedEgg);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <main className={styles.grid}>
      <header className={styles.header}>
        <h1 className="m-0 heading-default text-100">Hatchening</h1>
        <p className="m-0 body-large text-60">
          Select up to <span className="text-100">{MAX_CONCURRENT_HATCH_LIMIT} eggs</span> and hatch
          your Gribble!
        </p>
      </header>
      <Sidebar />
      <Preview />
      <MobileEggSelection />
      <WaitingModal />
    </main>
  );
}

function WaitingModal() {
  const [state] = useHatchening();

  if (!state.matches('hatchingEggs.waiting')) {
    return null;
  }

  return <HatchingModal />;
}

const selectSteps: HatcheningState[] = [
  'loadingEggs',
  'selectingEggs',
  'hatchingEggs',
  'playingAnimation',
];

export function SelectEggStep() {
  return (
    <HatcheningStep steps={selectSteps}>
      <SelectEgg />
    </HatcheningStep>
  );
}

export function NoEggStep() {
  return (
    <HatcheningStep steps="noEggsToHatch">
      <div className={styles.noEggsToHatchContainer}>
        <CollectionEmptyState filterType="eggs" />
      </div>
    </HatcheningStep>
  );
}
